/** Gentle, user-approved service-worker updates for installed PWAs. */

type UpdateListener = (applyUpdate: () => void) => void;

const updateListeners = new Set<UpdateListener>();
let pendingApplyUpdate: (() => void) | null = null;

export function subscribeToServiceWorkerUpdate(listener: UpdateListener): () => void {
  updateListeners.add(listener);
  if (pendingApplyUpdate !== null) listener(pendingApplyUpdate);
  return () => {
    updateListeners.delete(listener);
  };
}

function announceUpdate(applyUpdate: () => void): void {
  pendingApplyUpdate = applyUpdate;
  for (const listener of updateListeners) listener(applyUpdate);
}

export function registerProductionServiceWorker(): void {
  if (!("serviceWorker" in navigator) || !import.meta.env.PROD) return;

  const register = () => {
    void navigator.serviceWorker
      .register("/sw.js", { updateViaCache: "none" })
      .then((registration) => {
        const notifyWaiting = () => {
          if (registration.waiting === null) return;
          announceUpdate(() => {
            registration.waiting?.postMessage({ type: "SKIP_WAITING" });
            // Reload after the user taps Restart. No silent clients.claim().
            window.setTimeout(() => {
              window.location.reload();
            }, 60);
          });
        };

        notifyWaiting();
        registration.addEventListener("updatefound", () => {
          const worker = registration.installing;
          if (worker === null) return;
          worker.addEventListener("statechange", () => {
            if (worker.state === "installed" && navigator.serviceWorker.controller) notifyWaiting();
          });
        });

        const checkForUpdate = () => {
          if (document.visibilityState !== "visible") return;
          void registration.update().catch(() => undefined);
        };
        document.addEventListener("visibilitychange", checkForUpdate);
        window.addEventListener("pageshow", checkForUpdate);
        void registration.update().catch(() => undefined);
      })
      .catch(() => undefined);
  };

  if (document.readyState === "complete") register();
  else window.addEventListener("load", register, { once: true });
}
